import React from "react";
import { Award, BadgeCheck, Sparkles, UserCheck, Star } from "lucide-react";

const META = {
  self_rated: { label: "Self-rated", icon: Star, desc: "The player set this level themselves." },
  peer_confirmed: { label: "Peer-confirmed", icon: UserCheck, desc: "Confirmed by players they've matched with (3+ sessions)." },
  session_validated: { label: "Session-validated", icon: Sparkles, desc: "Validated across 10+ completed sessions." },
  organizer_verified: { label: "Organizer-verified", icon: BadgeCheck, desc: "Verified in person by an ActivitySports organizer." },
  external_rating: { label: "External rating", icon: Award, desc: "Backed by an official external rating (NTRP / DUPR / UTR)." },
};

// Light (white card) — AA-contrast dark text on light bg.
const LIGHT = {
  self_rated: "bg-slate-100 text-slate-600 border-slate-300",
  peer_confirmed: "bg-blue-50 text-blue-700 border-blue-300",
  session_validated: "bg-cyan-50 text-cyan-800 border-cyan-300",
  organizer_verified: "bg-green-50 text-green-700 border-green-300",
  external_rating: "bg-amber-50 text-amber-800 border-amber-300",
};

// Dark surfaces — AA-contrast light text on dark translucent bg.
const DARK = {
  self_rated: "bg-slate-500/15 text-slate-300 border-slate-400/40",
  peer_confirmed: "bg-blue-500/15 text-blue-300 border-blue-400/40",
  session_validated: "bg-cyan-500/15 text-cyan-300 border-cyan-400/40",
  organizer_verified: "bg-green-500/15 text-green-300 border-green-400/40",
  external_rating: "bg-amber-500/15 text-amber-300 border-amber-400/40",
};

export const CONFIDENCE_CONFIG = META;

export default function SkillConfidenceBadge({ level, label, size = "sm", variant = "light" }) {
  const key = META[level] ? level : "self_rated";
  const meta = META[key];
  const cls = (variant === "dark" ? DARK : LIGHT)[key];
  const Icon = meta.icon;
  const iconSize = size === "xs" ? 10 : 12;
  const px = size === "xs" ? "px-1.5 py-0.5" : "px-2 py-0.5";
  const text = `Skill confidence: ${label || meta.label}. ${meta.desc}`;

  return (
    <span className="relative inline-flex group" data-testid="skill-confidence-badge">
      <span
        className={`inline-flex items-center gap-1 ${px} rounded-full border ${cls} text-xs font-semibold whitespace-nowrap cursor-help`}
        role="img" aria-label={text} title={text} tabIndex={0}
      >
        <Icon size={iconSize} aria-hidden="true" />
        {label || meta.label}
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-30 mt-1 w-52 rounded-lg bg-brand-navy px-3 py-2 text-[11px] leading-snug text-white opacity-0 shadow-xl border border-white/10 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 dark-section"
      >
        {meta.desc}
      </span>
    </span>
  );
}
